import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatText } from 'core/i18n';
import { base64ToBlob, downloadAsFile } from 'utilities/APIRequestHelper';
import { printPdf } from 'utilities/pdf';
import { DocumentsModalScrollInfo } from 'components/newAccountSteps/initialStep/documentation/documentsModal/DocumentsModalScrollInfo';
import { useTimeoutFunction } from 'hooks/useTimeoutFunction';
import { PdfViewer } from './PdfViewer';
import { BLANK_PDF } from './blankPDF.js';

export const PdfModalComponent = ({
    scrollInfoModalTimeout = 15000,
    onDownloadFileName = 'document.pdf',
    path,
    showModal,
    toggleModal,
    isDocumentRead,
    onDocumentRead,
    isDocumentAccepted,
    onDocumentAccepted,
    initialActions,
    pdfFile,
}) => {
    const [showScrollInfoModal, setShowScrollInfoModal] = useState(false);
    const [pdfViewerHeight, setPdfViewerHeight] = useState(null);
    const [pdfSettings, setPdfSettings] = useState({});
    const pdfViewerWrapperRef = useRef(null);

    const buildBase64Pdf = () => `data:application/pdf;base64,${pdfFile ? pdfFile : BLANK_PDF}`;

    const pdfActions = {
        confirm: e => {
            e.preventDefault();
            onDocumentAccepted(e);
        },
        download: () => {
            downloadAsFile(base64ToBlob(buildBase64Pdf()), 'application/pdf', onDownloadFileName);
        },
        print: () => {
            printPdf(buildBase64Pdf());
        },
    };

    useEffect(() => {
        if (!showModal) {
            setShowScrollInfoModal(false);
        }
    }, [showModal]);

    useEffect(() => {
        initialActions.getPdfMerge([path]);
    }, [initialActions, path]);

    useEffect(() => {
        if (pdfViewerWrapperRef.current && pdfViewerHeight === null) {
            const { height } = pdfViewerWrapperRef.current.getBoundingClientRect();
            setPdfViewerHeight(height);
        }
    }, [pdfViewerHeight]);

    const calculatePageHeight = (page, width) => {
        const viewPort = page.getViewport();

        return viewPort.viewBox[3] * (width / viewPort.viewBox[2]);
    };

    const calculateScale = (page, width) => {
        const viewPort = page.getViewport();

        return width / viewPort.viewBox[2];
    };

    const handleLoadedDocumentLoadSuccess = async (pdfObject, width) => {
        const page = await pdfObject.getPage(1);
        const pageHeight = calculatePageHeight(page, width);
        const scale = calculateScale(page, width);
        setPdfSettings({ numPages: pdfObject.numPages, pageHeight, scale });
    };

    const toggleShowScrollInfoModal = () => {
        setShowScrollInfoModal(prevState => !prevState);
    };

    const handleClickCancelButtonModalScrollInfo = () => {
        toggleModal();
        toggleShowScrollInfoModal();
    };
    const handleUserInactivity = () => {
        toggleShowScrollInfoModal();
    };

    const { resetTimeoutFunction, startTimeoutFunction, stopTimeoutFunction } = useTimeoutFunction(
        handleUserInactivity,
        scrollInfoModalTimeout
    );
    const handleScroll = ({ clientHeight, scrollHeight, scrollTop }) => {
        const isStillScrollingDown = scrollTop + clientHeight < scrollHeight - 5;

        if (isStillScrollingDown) {
            return resetTimeoutFunction();
        }

        onDocumentRead();
        stopTimeoutFunction();
    };

    useEffect(() => {
        if (isDocumentAccepted) {
            stopTimeoutFunction();
        } else if (!isDocumentRead && showModal && !showScrollInfoModal) {
            startTimeoutFunction();
        }
    }, [isDocumentRead, isDocumentAccepted, showModal, showScrollInfoModal]);

    return (
        <>
            {showModal && (
                <div className={'documents__modal__box-outside modal-open'}>
                    <div className={'documents__modal__box-inside'}>
                        <div className="documents__modal__container">
                            <div className="documents__modal-heading">
                                <h4>{formatText('field-docs_title')}</h4>
                            </div>
                            <div
                                ref={pdfViewerWrapperRef}
                                className={`documents__modal-pdf-viewer ${isDocumentRead && 'with-icons-box'}`}
                            >
                                <PdfViewer
                                    pdfFile={buildBase64Pdf()}
                                    handleLoadedDocumentLoadSuccess={handleLoadedDocumentLoadSuccess}
                                    handleScroll={handleScroll}
                                    pdfSettings={pdfSettings}
                                />
                            </div>

                            {isDocumentRead && (
                                <div className="documents__modal-icons">
                                    <button type="button" onClick={pdfActions.print}>
                                        <i className={`icons icons--icon-print-blue`} />
                                    </button>
                                    <button type="button" onClick={pdfActions.download}>
                                        <i className={`icons icons--icon-download-blue`} />
                                    </button>
                                </div>
                            )}
                            <div className="documents__modal-buttons">
                                <span>
                                    {isDocumentAccepted ? (
                                        <button type="button" onClick={toggleShowScrollInfoModal}>
                                            <p aria-label="close-btn">{formatText('button-close')}</p>
                                        </button>
                                    ) : (
                                        <button type="button" onClick={toggleShowScrollInfoModal}>
                                            <p aria-label="close-btn">{formatText('button-goBack')}</p>
                                        </button>
                                    )}
                                </span>
                                {isDocumentRead ? (
                                    <button
                                        className="form__button form__button-secondary"
                                        onClick={pdfActions.confirm}
                                        disabled={!isDocumentRead}
                                        aria-label="confirm-btn"
                                        type="button"
                                    >
                                        {isDocumentAccepted
                                            ? formatText('button-understood')
                                            : formatText('button-acceptConditions')}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="form__button  disable-styles"
                                        aria-label="confirm-btn"
                                    >
                                        {formatText('button-acceptConditions')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showScrollInfoModal && (
                <DocumentsModalScrollInfo
                    handleClickCloseButton={toggleShowScrollInfoModal}
                    handleClickCancelButton={handleClickCancelButtonModalScrollInfo}
                    handleClickContinueButton={toggleShowScrollInfoModal}
                />
            )}
        </>
    );
};

PdfModalComponent.propTypes = {
    scrollInfoModalTimeout: PropTypes.number,
    onDownloadFileName: PropTypes.string,
    path: PropTypes.string,
    showModal: PropTypes.bool,
    toggleModal: PropTypes.func,
    isDocumentAccepted: PropTypes.bool,
    onDocumentAccepted: PropTypes.func,
    isDocumentRead: PropTypes.bool,
    onDocumentRead: PropTypes.func,
    goBack: PropTypes.func,
    initialActions: PropTypes.object,
    pdfFile: PropTypes.string,
};
